import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SignalIcon, WifiIcon, BoltIcon, ShieldCheckIcon, HomeIcon } from '@heroicons/react/24/outline';

const HowItWorks: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.currentTime = 0;
      video.play().catch(() => {});

      const handleTimeUpdate = () => {
        if (video.currentTime >= 6) {
          video.currentTime = 0;
        }
      };

      video.addEventListener('timeupdate', handleTimeUpdate);
      return () => video.removeEventListener('timeupdate', handleTimeUpdate);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-airmax-blue via-blue-700 to-blue-900 text-white py-8 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
              How Airmax Internet Works
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              High-speed wireless internet delivered directly to your home using cutting-edge technology
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works Video with Explanation */}
      <section className="py-6 md:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Simple 3-Step Setup */}
          <div className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6">Simple 3-Step Setup</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="flex gap-3 items-start">
                <span className="w-9 h-9 bg-airmax-blue text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</span>
                <div>
                  <h3 className="font-bold text-gray-900">Tower Signal</h3>
                  <p className="text-sm text-gray-600">Our towers broadcast a focused wireless signal across the service area directly to your home.</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <span className="w-9 h-9 bg-airmax-blue text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</span>
                <div>
                  <h3 className="font-bold text-gray-900">Home Receiver</h3>
                  <p className="text-sm text-gray-600">A small antenna on your roof picks up the signal and delivers high-speed internet to your home.</p>
                </div>
              </div>
              <div className="flex gap-3 items-start">
                <span className="w-9 h-9 bg-airmax-blue text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</span>
                <div>
                  <h3 className="font-bold text-gray-900">WiFi Throughout</h3>
                  <p className="text-sm text-gray-600">A router distributes WiFi to all your devices — phones, laptops, TVs, and more.</p>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden shadow-xl max-w-6xl mx-auto relative"
          >
            <video
              ref={videoRef}
              className="w-full block"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              controls={false}
              style={{ objectFit: 'cover', width: '100%' }}
            >
              <source src="/videos/howitworksvideo.mp4" type="video/mp4" />
            </video>
            {/* Text overlay on video - only on desktop */}
            <div className="absolute inset-0 hidden md:flex items-start justify-center px-6" style={{ paddingTop: '20px' }}>
              <div className="text-white text-center max-w-2xl">
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 drop-shadow-lg">From Our Tower to Your Home</h3>
                <p className="text-sm md:text-base lg:text-lg drop-shadow-lg leading-relaxed">
                  See how Airmax delivers high-speed internet — beaming a powerful wireless signal from our towers directly to a small receiver installed at your home. No cables, no digging, just fast and reliable connectivity.
                </p>
              </div>
            </div>
          </motion.div>
          {/* Text below video - only on mobile */}
          <div className="block md:hidden mt-6 text-center px-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">From Our Tower to Your Home</h3>
            <p className="text-base text-gray-600 leading-relaxed">
              See how Airmax delivers high-speed internet — beaming a powerful wireless signal from our towers directly to a small receiver installed at your home. No cables, no digging, just fast and reliable connectivity.
            </p>
          </div>

        </div>
      </section>

      {/* Wave 60 Technology Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="inline-block bg-blue-100 text-airmax-blue px-4 py-1 rounded-full text-sm font-semibold mb-4">
                Our Technology
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Advanced Wireless Technology
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Airmax uses cutting-edge point-to-multipoint wireless radios that deliver multi-gigabit speeds with incredibly low latency. Our carrier-grade technology ensures you get the fastest, most reliable internet connection possible.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <BoltIcon className="h-6 w-6 text-airmax-blue flex-shrink-0 mt-0.5 mr-3" />
                  <div>
                    <strong className="text-gray-900">Multi-Gigabit Speeds</strong>
                    <p className="text-gray-600">Our wireless technology delivers speeds up to 1 Gbps to your home</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <ShieldCheckIcon className="h-6 w-6 text-airmax-blue flex-shrink-0 mt-0.5 mr-3" />
                  <div>
                    <strong className="text-gray-900">Interference Free</strong>
                    <p className="text-gray-600">Dedicated frequency bands that are virtually interference-free for consistent performance</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <SignalIcon className="h-6 w-6 text-airmax-blue flex-shrink-0 mt-0.5 mr-3" />
                  <div>
                    <strong className="text-gray-900">Low Latency</strong>
                    <p className="text-gray-600">Sub-millisecond latency perfect for gaming, video calls, and real-time applications</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <WifiIcon className="h-6 w-6 text-airmax-blue flex-shrink-0 mt-0.5 mr-3" />
                  <div>
                    <strong className="text-gray-900">Focused Beam</strong>
                    <p className="text-gray-600">Narrow beam technology provides a dedicated connection to each home</p>
                  </div>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg w-full max-w-md">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-airmax-blue rounded-full flex items-center justify-center mx-auto mb-4">
                    <SignalIcon className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Airmax Wireless</h3>
                  <p className="text-gray-500 mt-1">High-Performance Radio</p>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center bg-white rounded-lg px-4 py-3 shadow-sm">
                    <span className="text-gray-600 text-sm font-medium">Throughput</span>
                    <span className="text-airmax-blue font-bold">Up to 1 Gbps</span>
                  </div>
                  <div className="flex justify-between items-center bg-white rounded-lg px-4 py-3 shadow-sm">
                    <span className="text-gray-600 text-sm font-medium">Connection</span>
                    <span className="text-airmax-blue font-bold">Dedicated</span>
                  </div>
                  <div className="flex justify-between items-center bg-white rounded-lg px-4 py-3 shadow-sm">
                    <span className="text-gray-600 text-sm font-medium">Latency</span>
                    <span className="text-airmax-blue font-bold">&lt; 1ms</span>
                  </div>
                  <div className="flex justify-between items-center bg-white rounded-lg px-4 py-3 shadow-sm">
                    <span className="text-gray-600 text-sm font-medium">Interference</span>
                    <span className="text-green-600 font-bold">None</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Wireless Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Wireless Internet?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fixed wireless technology brings high-speed internet to areas where traditional cable and fiber aren't available
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: BoltIcon,
                title: 'Fast Installation',
                desc: 'Get connected in as little as 1-2 days. No waiting weeks for cable or fiber trenching.'
              },
              {
                icon: ShieldCheckIcon,
                title: 'Reliable Service',
                desc: 'Carrier-grade equipment designed for 24/7 operation in Arizona weather conditions.'
              },
              {
                icon: SignalIcon,
                title: 'Strong Signal',
                desc: 'Dedicated point-to-point connection means consistent speeds without neighborhood congestion.'
              },
              {
                icon: WifiIcon,
                title: 'Whole Home WiFi',
                desc: 'Our included router provides strong WiFi coverage throughout your entire home.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow p-6 text-center"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-7 w-7 text-airmax-blue" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-airmax-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Connected?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Check if Airmax Internet is available at your address today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-white text-airmax-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors"
              >
                Check Availability
              </Link>
              <Link
                to="/plans"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-airmax-blue transition-colors"
              >
                View Plans
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
